import ErrorBoundary from "../../../SimpleComponents/Errors/ErrorBoundary";
import { TimeAxisStyles, TimelinesStyles } from "../../TimelinesStyles";
import TimeAxis from "../../Timeline/TimeAxis";
import ConnectedTimeline from "../../Timeline/ConnectedTimeline";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useGetPageFromPath,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import useContainerDimensions from "utils/useContainerDimensions";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import RetrotagPopper from "../../Timeline/RetrotagPopper/RetrotagPopper";
import { useIsRetrotagPopperOpen } from "utils/customHooks/retrotagSliceSelectors";
import { useIsDarkMode } from "store/atoms";
import { useIsTimelineExpanded } from "./singleTimelineStore";
import { useMediaQuery } from "@mui/material";
import { useEffect, useTransition } from "react";
import { useSelector } from "store/useSelector";
import { BREAKPOINT_DESKTOP_XL } from "constants/constants";
import { useNumVisibleSubTimelines } from "components/Dashboard/Timeline/TimelineCardControls/timelinesStore";
import { getTimelineOffsetY } from "components/Dashboard/Timeline/SkuOperatorTimeline/skuOperatorTimelineUtils";
import { safariOffset } from "components/Dashboard/Timeline/SkuOperatorTimeline/safariOffset";
import { BelowSingleTimelineRows } from "./BelowSingleTimelineRows";

const SingleTimeline = ({ containerDimensionsWidth }) => {
  const ckey = useCustomerKey();
  const { shouldUseCustomTimeZone, timeZoneLocaleString } = useSettings();
  const [ref, dimensions] = useContainerDimensions();
  const width = dimensions.width || 0;

  const visibleProcesses = useVisibleProcesses({
    getSingleProcessOnly: true,
  });

  const process = visibleProcesses[0] || {
    id: null,
    name: null,
    ckey: null,
  };

  const isRetrotagPopperOpen = useIsRetrotagPopperOpen(process.id);

  const [isDarkMode] = useIsDarkMode();

  const { isHubletPage, isGridLayoutPage } = useGetPageFromPath();
  const [isPendingOriginal, startTransition] = useTransition();
  const isPrint = useMediaQuery("print");
  const isPending = isPendingOriginal && !isPrint; // don't show loading state in print media

  // reset the tooltip/axis transforms AFTER the data is fetched (so we don't see it snap back to its initial position before loading new data)
  useEffect(() => {
    if (!isPending) {
      const timeAxisSvg = document.querySelector("#timeAxisSvg");

      timeAxisSvg?.setAttribute("transform", `translate(0, 0)`);
      // reset the timeline & tooltip transforms
      const tooltip = document.querySelector<HTMLDivElement>(
        `[data-component-name="TimelineTooltip"]`
      );
      if (tooltip && window.innerWidth < BREAKPOINT_DESKTOP_XL) {
        tooltip.style.transform = `translate(${0}px, 0)`;
      }
    }
  }, [isPending]);
  const timeAxisContext = useSelector(state => state.legacy.timeAxisContext);
  const timeAxisWidth = isPrint
    ? 900 // hardcode print width
    : dimensions
      ? width
      : // fall back to container dimensions width on first render
        containerDimensionsWidth;
  const [isTimelineExpanded] = useIsTimelineExpanded();
  const subTimelinesScrollPaddingBottom = useSubTimelinesScrollPaddingBottom();
  return (
    <>
      <RetrotagPopper process={process} />
      <TimelinesStyles
        ref={ref}
        className={`TimelinesStyles detailsPage tooltipOpen${
          isRetrotagPopperOpen ? " isRetrotagging" : ""
        }${isTimelineExpanded ? " isTimelineExpanded" : ""}`}
        $isDarkMode={isDarkMode}
        id="timelineStyles"
        $isPending={isPending}
      >
        <TimeAxisStyles
          id="TimeAxisStyles"
          className={`TimeAxisStyles detailsPage${isTimelineExpanded ? "" : " collapsed"}`}
          data-server-names={[SERVER_NAMES.askrSegments]}
          $isDarkMode={isDarkMode}
        >
          <ErrorBoundary id={"TimeAxis"}>
            <TimeAxis
              isDarkMode={isDarkMode}
              startTransition={startTransition}
              width={timeAxisWidth}
              zoomable={true}
              startSliding={false}
              customerKey={ckey}
              shouldUseCustomTimeZone={shouldUseCustomTimeZone}
              timeZoneLocaleString={timeZoneLocaleString}
              isSingleTimeline={true}
              subTimelinesScrollPaddingBottom={subTimelinesScrollPaddingBottom}
              {...{
                isHubletPage,
                isMultipleTimelinesPage: false,
                isGridLayoutPage,
              }}
              {...timeAxisContext}
            >
              <ConnectedTimeline
                process={process}
                timelineIdx={0}
                width={timeAxisWidth}
              />
            </TimeAxis>
          </ErrorBoundary>
        </TimeAxisStyles>
        <BelowSingleTimelineRows />
      </TimelinesStyles>
    </>
  );
};

export default SingleTimeline;

function useSubTimelinesScrollPaddingBottom() {
  const [isTimelineExpanded] = useIsTimelineExpanded();
  const numVisibleSubTimelines = useNumVisibleSubTimelines();
  const subTimelinesScrollPaddingBottom = isTimelineExpanded
    ? getTimelineOffsetY(numVisibleSubTimelines) - 20 - 1.5 * safariOffset
    : 0;
  return subTimelinesScrollPaddingBottom;
}
